import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Eye, Users, MapPin, Globe, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { t, locale } from "@/i18n";

const regionName = (cc, fallback) => { try { return cc ? new Intl.DisplayNames([locale], { type: "region" }).of(cc) : fallback; } catch { return fallback; } };
const flag = (cc) => (cc && cc.length === 2 ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))) : "");
const dt = (iso) => new Date(iso).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-xl border bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-center justify-between"><p className="eyebrow text-[10px] sm:text-xs">{label}</p><Icon className="h-4 w-4 text-muted-foreground" /></div>
      <p className="num mt-2 text-2xl font-bold" data-testid={`${testId}-value`}>{value}</p>
    </div>
  );
}

function Panel({ title, children, testId, className = "" }) {
  return (
    <div className={`min-w-0 rounded-xl border bg-card p-4 sm:p-5 ${className}`} data-testid={testId}>
      <h2 className="mb-4 text-sm font-semibold">{title}</h2>
      {children}
    </div>
  );
}

function BarList({ rows, label, value, sub, empty, testPrefix }) {
  if (!rows.length) return <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p>;
  const max = Math.max(...rows.map(value), 1);
  return (
    <ul className="space-y-2.5">
      {rows.map((r, i) => (
        <li key={i} data-testid={`${testPrefix}-${i}`}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate">{label(r)}</span>
            <span className="num shrink-0 text-xs text-muted-foreground">{sub ? sub(r) : value(r)}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${(value(r) / max) * 100}%` }} /></div>
        </li>
      ))}
    </ul>
  );
}

export default function AdminVisitors() {
  const { user } = useAuth();
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return;
    setData(null);
    setError(false);
    api.get("/admin/visitors", { params: { days } }).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [user, days]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <>
      <PageHeader eyebrow={t("common.administration")} title={t("common.visiteurs")} description={t("adminVisitors.d_ou_viennent_vos_visiteurs_ville_estimee")}
        actions={
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="sm:w-44" data-testid="visitors-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">{t("adminVisitors.24_dernieres_heures")}</SelectItem>
              <SelectItem value="7">{t("adminVisitors.7_derniers_jours")}</SelectItem>
              <SelectItem value="30">{t("adminVisitors.30_derniers_jours")}</SelectItem>
              <SelectItem value="90">{t("adminVisitors.90_derniers_jours")}</SelectItem>
            </SelectContent>
          </Select>
        } />
      {error ? (
        <p className="py-16 text-center text-sm text-muted-foreground" data-testid="visitors-error">{t("adminVisitors.impossible_de_charger_les_statistiques_ree")}</p>
      ) : !data ? (
        <div className="flex h-64 items-center justify-center" data-testid="visitors-loading"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <Stat icon={Users} label={t("adminVisitors.visiteurs_uniques")} value={data.totals.visitors} testId="visitors-stat-unique" />
            <Stat icon={Eye} label={t("adminVisitors.pages_vues")} value={data.totals.visits} testId="visitors-stat-visits" />
            <Stat icon={MapPin} label={t("adminVisitors.villes")} value={data.totals.cities} testId="visitors-stat-cities" />
            <Stat icon={Globe} label={t("adminVisitors.pays")} value={data.totals.countries} testId="visitors-stat-countries" />
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Panel title={t("adminVisitors.visiteurs_par_ville")} testId="visitors-cities-panel" className="lg:col-span-2">
              <BarList rows={data.cities.slice(0, 15)} testPrefix="visitors-city" empty={t("adminVisitors.aucune_visite_sur_la_periode")}
                label={(r) => <>{flag(r.country_code)} <span className="font-medium">{r.city}</span>{r.country && <span className="hidden text-muted-foreground sm:inline"> · {regionName(r.country_code, r.country)}</span>}</>}
                value={(r) => r.visitors} sub={(r) => t("adminVisitors.x_visiteur_s_x_page_s", { v0: r.visitors, v1: r.visits })} />
            </Panel>
            <div className="space-y-4">
              <Panel title={t("adminVisitors.pays")} testId="visitors-countries-panel">
                <BarList rows={data.countries.slice(0, 8)} testPrefix="visitors-country" empty="—"
                  label={(r) => <>{flag(r.country_code)} {regionName(r.country_code, r.country)}</>} value={(r) => r.visitors} />
              </Panel>
              <Panel title={t("adminVisitors.inscriptions_par_ville")} testId="visitors-signups-panel">
                <BarList rows={data.signup_cities} testPrefix="visitors-signup-city" empty={t("adminVisitors.aucune_inscription_sur_la_periode")}
                  label={(r) => r.city} value={(r) => r.signups} />
              </Panel>
            </div>
            <Panel title={t("adminVisitors.funnel_analyse_de_carte")} testId="visitors-menu-funnel-panel">
              <BarList rows={[[t("adminVisitors.analyses_realisees"), data.menu_funnel.analyses], [t("adminVisitors.rattachees_a_un_compte"), data.menu_funnel.claimed], [t("adminVisitors.plats_ajoutes_aux_recettes"), data.menu_funnel.imported]]} testPrefix="visitors-menu-funnel" empty="—"
                label={(r) => r[0]} value={(r) => r[1]} />
            </Panel>
            <Panel title={t("adminVisitors.pages_les_plus_vues")} testId="visitors-pages-panel">
              <BarList rows={data.pages} testPrefix="visitors-page" empty="—" label={(r) => <span className="font-mono text-xs">{r.path}</span>} value={(r) => r.visits} />
            </Panel>
            <Panel title={t("adminVisitors.sites_referents")} testId="visitors-referrers-panel">
              <BarList rows={data.referrers} testPrefix="visitors-referrer" empty={t("adminVisitors.acces_direct_uniquement")} label={(r) => r.host} value={(r) => r.visits} />
            </Panel>
            <Panel title={t("adminVisitors.dernieres_visites")} testId="visitors-recent-panel">
              {data.recent.length ? (
                <ul className="divide-y text-sm">
                  {data.recent.slice(0, 12).map((v, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 py-2" data-testid={`visitors-recent-${i}`}>
                      <span className="min-w-0 truncate">{flag(v.country_code)} {v.city || t("adminVisitors.inconnue")} <span className="font-mono text-xs text-muted-foreground">{v.path}</span></span>
                      <span className="shrink-0 text-xs text-muted-foreground">{dt(v.created_at)}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="py-6 text-center text-sm text-muted-foreground">—</p>}
            </Panel>
          </div>
        </>
      )}
    </>
  );
}
